import type { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './BoardViewDock.module.css';

export interface BoardView {
  /** Stable id (`timeline`, later `calendar`, `table`, ...). */
  id: string;
  label: string;
  icon: ReactNode;
  active: boolean;
  onSelect: () => void;
}

interface Props {
  views: readonly BoardView[];
  ref?: Ref<HTMLElement>;
  /** Part of the page header instead of floating at the bottom (a phone on its side). */
  inline?: boolean;
}

/**
 * Floating dock at the bottom of the board that opens and closes board views. It knows nothing
 * about any view: it renders whatever `views` it is given (today only Timeline; Calendar, Table
 * and others can be added by the caller). The active view is marked with `aria-pressed`.
 */
export function BoardViewDock({ views, ref, inline = false }: Props) {
  const { t } = useTranslation();
  if (views.length === 0) return null;
  return (
    <nav
      ref={ref}
      className={`${styles.dock} ${inline ? styles.inline : ''}`}
      aria-label={t('dock.label')}
      data-testid="board-view-dock"
    >
      <ul className={`${styles.items} scroll-strip`}>
        {views.map((view) => (
          <li key={view.id}>
            <button
              type="button"
              className={`${styles.item} ${view.active ? styles.active : ''}`}
              data-view={view.id}
              title={view.label}
              aria-pressed={view.active}
              onClick={view.onSelect}
            >
              <span className={styles.icon} aria-hidden>
                {view.icon}
              </span>
              <span className={styles.text}>{view.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
